import React, { useState } from 'react';
import axios from 'axios';
import { Send, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const serviceOptions = [
  'Residential Electrical',
  'Commercial Electrical',
  'Panel Upgrades',
  'EV Charger Installation',
  'Power Distribution',
  'Maintenance & Repairs',
  'Other'
];

const initialForm = { name: '', email: '', phone: '', service: '', message: '' };

const ContactForm = () => {
  const [form, setForm] = useState(initialForm);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.service || !form.message) {
      toast.error('Please fill in all fields.');
      return;
    }
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thanks! We've received your request and will be in touch shortly.");
      setForm(initialForm);
    } catch (err) {
      toast.error('Something went wrong. Please call us directly or try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      data-testid="contact-form"
      className="bg-neutral-950 border border-white/10 p-8 lg:p-10 max-w-xl mx-auto text-left"
    >
      <div className="grid sm:grid-cols-2 gap-5 mb-5">
        <div>
          <label className="block text-sm text-neutral-400 mb-2">Full Name</label>
          <input
            data-testid="contact-name-input"
            name="name"
            value={form.name}
            onChange={handleChange}
            type="text"
            placeholder="John Doe"
            className="w-full bg-black border border-white/10 px-4 py-3 text-white placeholder-neutral-600 focus:border-electric-yellow transition-colors duration-300"
          />
        </div>
        <div>
          <label className="block text-sm text-neutral-400 mb-2">Phone</label>
          <input
            data-testid="contact-phone-input"
            name="phone"
            value={form.phone}
            onChange={handleChange}
            type="tel"
            placeholder="(647) 000-0000"
            className="w-full bg-black border border-white/10 px-4 py-3 text-white placeholder-neutral-600 focus:border-electric-yellow transition-colors duration-300"
          />
        </div>
      </div>

      <div className="mb-5">
        <label className="block text-sm text-neutral-400 mb-2">Email</label>
        <input
          data-testid="contact-email-input"
          name="email"
          value={form.email}
          onChange={handleChange}
          type="email"
          placeholder="you@example.com"
          className="w-full bg-black border border-white/10 px-4 py-3 text-white placeholder-neutral-600 focus:border-electric-yellow transition-colors duration-300"
        />
      </div>

      <div className="mb-5">
        <label className="block text-sm text-neutral-400 mb-2">Service Needed</label>
        <select
          data-testid="contact-service-select"
          name="service"
          value={form.service}
          onChange={handleChange}
          className="w-full bg-black border border-white/10 px-4 py-3 text-white focus:border-electric-yellow transition-colors duration-300"
        >
          <option value="">Select a service</option>
          {serviceOptions.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </div>

      <div className="mb-8">
        <label className="block text-sm text-neutral-400 mb-2">Message</label>
        <textarea
          data-testid="contact-message-input"
          name="message"
          value={form.message}
          onChange={handleChange}
          rows={4}
          placeholder="Tell us about your project..."
          className="w-full bg-black border border-white/10 px-4 py-3 text-white placeholder-neutral-600 focus:border-electric-yellow transition-colors duration-300 resize-none"
        />
      </div>

      <button
        data-testid="contact-submit-button"
        type="submit"
        disabled={isSubmitting}
        className="w-full inline-flex items-center justify-center gap-3 bg-electric-yellow text-black px-8 py-4 text-lg font-semibold hover:bg-electric-yellow/10 hover:text-electric-yellow border-2 border-electric-yellow transition-all duration-400 disabled:opacity-60"
      >
        {isSubmitting ? (
          <>
            <Loader2 size={20} className="animate-spin" />
            Sending...
          </>
        ) : (
          <>
            Send Request
            <Send size={18} />
          </>
        )}
      </button>
    </form>
  );
};

export default ContactForm;
